import { ReactElement } from "react";
import {
  Cell,
  Column,
  Header,
  HeaderGroup,
  Row,
  RowData,
  Table as ReactTable,
  flexRender,
  ColumnPinningPosition,
} from "@tanstack/react-table";
import {
  Box,
  Flex,
  Table,
  TableBodyProps,
  TableCellProps,
  TableColumnHeaderProps,
  TableFooterProps,
  TableHeadProps,
  TableProps,
  TableRowProps,
  Tbody,
  Td,
  Tfoot,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { sum } from "lodash";
import TableLoadingSpinner, {
  TableLoadingSpinnerProps,
} from "./TableLoadingSpinner";
import SortControls from "./SortControls";
import { pinnedColumnStyles } from "./utils";

type ColumnProps = TableColumnHeaderProps | TableCellProps;

type PinningPosition = Exclude<ColumnPinningPosition, boolean>;

type PinnedDataAttributes = {
  /* eslint-disable @typescript-eslint/naming-convention */
  "data-pinning"?: PinningPosition;
  "data-pinning-index"?: number;
  "data-pinning-index-inside"?: number;
  "data-pinning-index-outside"?: number;
  /* eslint-enable @typescript-eslint/naming-convention */
};

type PinnedSizes = Record<PinningPosition, number[]>;

function getPinnedOffset(
  position: PinningPosition,
  pinnedIndex: number,
  pinnedSizes: PinnedSizes | null | undefined,
) {
  const sizes = pinnedSizes?.[position];

  if (!sizes) return 0;

  if (position === "left") {
    if (pinnedIndex === 0 || pinnedIndex >= sizes.length) return 0;
    return sum(sizes.slice(0, pinnedIndex));
  }

  if (position === "right") {
    if (pinnedIndex >= sizes.length - 1) return 0;
    return sum(sizes.slice(pinnedIndex + 1));
  }

  return 0;
}

function getHeaderOrCellProps<D extends RowData>(
  column: Column<D>,
  existing: ColumnProps,
  pinnedSizes?: PinnedSizes,
): ColumnProps & PinnedDataAttributes {
  const pinning = column.getIsPinned();

  if (!pinning) return existing;

  const sizes = pinnedSizes?.[pinning];
  const numPinned = sizes?.length || 0;
  const pinnedIndex = column.getPinnedIndex();
  const reverseIndex = numPinned - pinnedIndex - 1;

  return {
    backgroundColor: "inherit",
    ...existing,
    "data-pinning": pinning,
    "data-pinning-index": pinnedIndex,
    "data-pinning-index-inside":
      pinning === "left" ? reverseIndex : pinnedIndex,
    "data-pinning-index-outside":
      pinning === "left" ? pinnedIndex : reverseIndex,
    position: "sticky",
    zIndex: Math.max(
      1,
      typeof existing.zIndex === "number" ? existing.zIndex : 0,
    ),
    style: {
      ...existing?.style,
      [pinning === "left" ? "left" : "right"]: getPinnedOffset(
        pinning,
        pinnedIndex,
        pinnedSizes,
      ),
    },
  };
}

function getColumnSizes<D extends RowData>(columns: Column<D>[]) {
  return columns.map((col) => col.getSize());
}

function getPinnedColumnSizes<D extends RowData>(
  tableInstance: ReactTable<D>,
): PinnedSizes {
  const leftCols = tableInstance.getLeftVisibleLeafColumns();
  const rightCols = tableInstance.getRightVisibleLeafColumns();
  return { left: getColumnSizes(leftCols), right: getColumnSizes(rightCols) };
}

export type ComponentProps<D extends RowData> = {
  LoadingComponent?: React.ComponentType<{ tableInstance: ReactTable<D> }>;
  colgroupElement?: ReactElement;
  getCellProps?: (cell: Cell<D, unknown>) => Partial<TableCellProps>;
  getColumnProps?: (
    column: Column<D>,
  ) => Partial<TableColumnHeaderProps | TableCellProps>;
  getFooterGroupProps?: (footerGroup: HeaderGroup<D>) => Partial<TableRowProps>;
  getFooterProps?: (footer: Header<D, unknown>) => Partial<TableCellProps>;
  getHeaderGroupProps?: (headerGroup: HeaderGroup<D>) => Partial<TableRowProps>;
  getHeaderProps?: (
    header: Header<D, unknown>,
  ) => Partial<TableColumnHeaderProps>;
  getRowProps?: (row: Row<D>) => Partial<TableRowProps> & {
    [dataAttribute: `data-${string}`]: unknown;
  };
  loadingSpinnerProps?: Omit<TableLoadingSpinnerProps<D>, "tableInstance">;
  tableBodyProps?: TableBodyProps;
  tableFootProps?: TableFooterProps;
  tableHeadProps?: TableHeadProps;
  tableProps?: TableProps;
  noRowsPlaceholderProps?: Partial<TableCellProps>;
  noRowsPlaceholderRowProps?: Partial<TableRowProps>;
};

export type Props<D extends RowData> = ComponentProps<D> & {
  children?: React.ReactNode;
  hasPinEdgeShadow?: boolean;
  hasFooter?: boolean;
  isLoading?: boolean;
  noRowsPlaceholder?: React.ReactNode;
  tableInstance: ReactTable<D>;
};

export default function ChakraReactTable<D extends object>({
  LoadingComponent = TableLoadingSpinner,
  children,
  colgroupElement,
  getCellProps,
  getColumnProps,
  getFooterGroupProps,
  getFooterProps,
  getHeaderGroupProps,
  getHeaderProps,
  getRowProps,
  hasFooter = false,
  hasPinEdgeShadow = true,
  isLoading = false,
  loadingSpinnerProps,
  noRowsPlaceholder,
  noRowsPlaceholderProps,
  noRowsPlaceholderRowProps,
  tableBodyProps,
  tableFootProps,
  tableHeadProps,
  tableInstance,
  tableProps,
}: Props<D>) {
  const { getHeaderGroups, getFooterGroups, getRowModel } = tableInstance;
  const { rows } = getRowModel();
  const numRows = rows?.length;
  const isPinned = tableInstance.getIsSomeColumnsPinned();
  const pinnedSizes = isPinned
    ? getPinnedColumnSizes(tableInstance)
    : undefined;
  return (
    <Table
      {...tableProps}
      sx={
        hasPinEdgeShadow
          ? { ...tableProps?.sx, ...pinnedColumnStyles }
          : tableProps?.sx
      }
    >
      {colgroupElement}
      <Thead {...tableHeadProps}>
        {getHeaderGroups().map((headerGroup) => (
          <Tr key={headerGroup.id} {...getHeaderGroupProps?.(headerGroup)}>
            {headerGroup.headers.map((header) => (
              <Th
                key={header.id}
                colSpan={header.colSpan}
                scope="col"
                {...getHeaderOrCellProps(
                  header.column,
                  {
                    ...getColumnProps?.(header.column),
                    ...header.column.columnDef.meta?.props,
                    ...header.column.columnDef.meta?.headerProps,
                    ...getHeaderProps?.(header),
                  },
                  pinnedSizes,
                )}
              >
                {!header.isPlaceholder &&
                  (tableInstance.options.enableSorting ? (
                    <Flex
                      className="sorting-enabled-header-wrapper"
                      display="inline-flex"
                      alignItems="center"
                      minW="0"
                      maxW="100%"
                      minH="1.55em"
                    >
                      {header.column.getCanSort() ? (
                        <>
                          <Box
                            className="sortable-header-content"
                            minW="0"
                            lineHeight="normal"
                            isTruncated
                          >
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )}
                          </Box>
                          <SortControls
                            isSorted={header.column.getIsSorted()}
                            onSortAscending={() =>
                              header.column.toggleSorting(false)
                            }
                            onSortDescending={() =>
                              header.column.toggleSorting(true)
                            }
                          />
                        </>
                      ) : (
                        flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )
                      )}
                    </Flex>
                  ) : (
                    flexRender(
                      header.column.columnDef.header,
                      header.getContext(),
                    )
                  ))}
              </Th>
            ))}
          </Tr>
        ))}
      </Thead>

      <Tbody
        {...tableBodyProps}
        backgroundColor={isPinned ? "inherit" : undefined}
      >
        {rows?.map((row) => (
          <Tr
            key={row.id}
            data-depth={row.depth}
            data-index={row.index}
            backgroundColor={isPinned ? "inherit" : undefined}
            {...getRowProps?.(row)}
          >
            {row.getVisibleCells().map((cell) => (
              <Td
                key={cell.id}
                {...getHeaderOrCellProps(
                  cell.column,
                  {
                    ...getColumnProps?.(cell.column),
                    ...cell.column.columnDef.meta?.props,
                    ...cell.column.columnDef.meta?.cellProps,
                    ...getCellProps?.(cell),
                    ...cell.column.columnDef.meta?.getCellProps?.(cell),
                  },
                  pinnedSizes,
                )}
              >
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </Td>
            ))}
          </Tr>
        ))}

        {noRowsPlaceholder && !numRows && !isLoading && (
          <Tr {...noRowsPlaceholderRowProps}>
            <Td
              colSpan={tableInstance.getAllFlatColumns()?.length}
              textAlign="center"
              fontSize="sm"
              color="gray.400"
              background="linear-gradient(180deg, rgba(186, 194, 253, 0.15) 0%, rgba(186, 194, 253, 0.05) 33%, transparent 80%)"
              py={10}
              className="scroll-container-centered"
              {...noRowsPlaceholderProps}
            >
              {noRowsPlaceholder}
            </Td>
          </Tr>
        )}

        {isLoading &&
          !numRows &&
          (LoadingComponent === TableLoadingSpinner ? (
            <TableLoadingSpinner
              tableInstance={tableInstance}
              {...loadingSpinnerProps}
            />
          ) : (
            <LoadingComponent tableInstance={tableInstance} />
          ))}

        {children}
      </Tbody>

      {hasFooter && (
        <Tfoot {...tableFootProps}>
          {getFooterGroups().map((footerGroup) => (
            <Tr key={footerGroup.id} {...getFooterGroupProps?.(footerGroup)}>
              {footerGroup.headers.map((footer) => (
                <Td
                  key={footer.id}
                  colSpan={footer.colSpan}
                  {...getHeaderOrCellProps(
                    footer.column,
                    {
                      ...getColumnProps?.(footer.column),
                      ...footer.column.columnDef.meta?.props,
                      ...footer.column.columnDef.meta?.footerProps,
                      ...getFooterProps?.(footer),
                    },
                    pinnedSizes,
                  )}
                >
                  {!footer.isPlaceholder &&
                    flexRender(
                      footer.column.columnDef.footer,
                      footer.getContext(),
                    )}
                </Td>
              ))}
            </Tr>
          ))}
        </Tfoot>
      )}
    </Table>
  );
}
